import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { createShopProductOrder, getShopProductOrders, getShopProducts } from "../../../api/player/shop";
import { useEventContext } from "./EventContext";
import { Event } from "../../../cp/components/Comet";
import cloneDeep from "lodash.clonedeep";
import { useUserContext } from "./UserContext";
import { getUserBalance } from "../utils/api";

export const ShopContext = createContext(null);
export const useShopContext = () => useContext(ShopContext);

export const ShopContextProvider = ({ children }) => {
    const { event, initData } = useEventContext();
    const { user } = useUserContext();
    const [prizes, setPrizes] = useState([]);
    const [orders, setOrders] = useState([]);
    const [creatingOrder, setCreatingOrder] = useState(false);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [userBalance, setUserBalance] = useState(initData?.balance || null);

    const moduleShopEnabled = event?.moduleShop?.enabled;
    const clientId = user?.id || user?.clientRegistrationId;

    const getPrizes = async () => {
        try {
            const products = await getShopProducts(event.eventKey);
            setPrizes(products || []);
        } catch (e) {
            console.error(e)
        }
    }

    const getOrders = async () => {
        if (!user) return;
        setLoadingOrders(true);
        try {
            const _orders = await getShopProductOrders(event.eventKey);
            setOrders(_orders || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingOrders(false);
        }
    }

    const createOrder = async (prizeID) => {
        if (creatingOrder) return; 
        setCreatingOrder(true);
        try {
            const response = await createShopProductOrder(event.eventKey, prizeID);
            getOrders();
            return response?.data;
        } catch (e) {
            console.error(e)
        } finally {
            setCreatingOrder(false);
        }
    }

    const orderedProducts = useMemo(() => {
        const products = {};
        orders.forEach(order => {
            products[order.productId] = (products[order.productId] || 0) + 1;
        });
        return products;
    }, [orders]);

    const sortedPrizes = useMemo(() => {
        return prizes
            .map(prize => ({
                ...prize,
                unavailableForUser: prize.limitPerPerson <= orderedProducts[prize.id],
            }))
            .sort((a, b) => a.sortOrder - b.sortOrder);
    }, [prizes, orderedProducts]);

    const activePrizes = useMemo(() => (
        sortedPrizes.filter(({ active }) => active)
    ), [sortedPrizes]);

    const updatePrizes = (product) => {
        setPrizes(prizes => {
            const _prizes = cloneDeep(prizes);
            const index = _prizes.findIndex(_prize => _prize.id === product.id);
            if (index < 0) {
                _prizes.push(product)
            } else {
                _prizes[index] = { ..._prizes[index], ...product }
            }
            return _prizes;
        })
    }

    const reorderPrizes = (sortOrder) => {
        setPrizes(prizes => prizes.map(prize => ({...prize, sortOrder: sortOrder.indexOf(prize.id) ?? prize.sortOrder})));
    }

    const getBalance = async () => {
        if (!moduleShopEnabled) return;
        try {
            const balance = await getUserBalance(event.eventKey);
            setUserBalance(balance);
        } catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        if (moduleShopEnabled) {
            getPrizes();
        } else {
            setPrizes([]);
        }
    }, [moduleShopEnabled]);

    useEffect(() => {
        if (clientId) {
            getBalance();
            getOrders();
        } else {
            setUserBalance(null);
            setOrders([]);
        }
    }, [clientId]);

    const context = {
        prizes: sortedPrizes,
        activePrizes,
        orders,
        getOrders,
        createOrder,
        userBalance,
        setUserBalance,
        loadingShopData: creatingOrder || loadingOrders,
    };

    return (
        <ShopContext.Provider value={context}>
            <Event event={`create:product`} handler={(product) => updatePrizes(product)} />
            <Event event={`update:product`} handler={(product) => updatePrizes(product)} />
            <Event event={`delete:product`} handler={(product) => setPrizes(prizes => prizes.filter(({ id }) => id !== product.id))} />
            <Event event={`reorder:products`} handler={(message) => reorderPrizes(message?.sortOrder || [])} />
            <Event event={"update:balance"} handler={({ balance }) => setUserBalance(balance)} />
            {children}
        </ShopContext.Provider>
    );
}
